import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Download, 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight,
  Save,
  Upload,
  X
} from 'lucide-react';
import { cn } from '../../utils/cn';

interface PdfStepProps {
  title: string;
  pdfUrl: string;
  pdfFile?: File | null;
  content?: string;
  onChange?: (content: string) => void;
  onPdfChange?: (url: string, file?: File | null) => void;
  onSave?: () => void;
}

const PdfStep: React.FC<PdfStepProps> = ({ 
  title, 
  pdfUrl, 
  pdfFile,
  content, 
  onChange,
  onPdfChange,
  onSave 
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(pdfFile || null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleDownload = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      const link = document.createElement('a');
      link.href = url;
      link.download = uploadedFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = title || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      const url = URL.createObjectURL(file);
      if (onPdfChange) {
        onPdfChange(url, file);
      }
      setShowUpload(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (onPdfChange) {
      onPdfChange('', null);
    }
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    } else if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Calculate current PDF URL
  const currentPdfUrl = uploadedFile ? URL.createObjectURL(uploadedFile) : (pdfUrl && pdfUrl.trim() ? pdfUrl : null);
  const pdfViewerUrl = currentPdfUrl ? `${currentPdfUrl}#page=${currentPage}&zoom=${zoom}&toolbar=0&navpanes=0&scrollbar=0` : '';

  // Handle PDF load events
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !currentPdfUrl) return;

    const handleLoad = () => {
      setIsLoading(false);
      setError(null);
    };

    const handleError = () => {
      setIsLoading(false);
      setError('Failed to load PDF. Please check the URL and try again.');
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
    };
  }, [currentPdfUrl]);

  return (
    <motion.div
      className={cn(
        "bg-white rounded-lg border border-gray-200 overflow-hidden",
        isFullscreen && "fixed inset-0 z-50 rounded-none"
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <h3 className="font-semibold">PDF Document</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="p-2 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
              title="Download PDF"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={handleFullscreen}
              className="p-2 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange('prev')}
              disabled={currentPage <= 1}
              className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="text-sm text-gray-600 px-2">
              Page {currentPage} {totalPages > 0 && `of ${totalPages}`}
            </span>
            
            <button
              onClick={() => handlePageChange('next')}
              disabled={currentPage >= totalPages}
              className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleZoomOut}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            
            <span className="text-sm text-gray-600 px-2 min-w-[60px] text-center">
              {zoom}%
            </span>
            
            <button
              onClick={handleZoomIn}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            <button
              onClick={handleRotate}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
              title="Rotate"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      {!currentPdfUrl && (
        <div className="border-b border-gray-200 p-6">
          <div className="text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No PDF selected</h3>
            <p className="text-gray-600 mb-6">Upload a PDF file or enter a URL to get started</p>
            
            <div className="max-w-md mx-auto space-y-4">
              {/* Upload File Option */}
              <div className="flex justify-center">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload PDF File</span>
                </button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
              
              {/* Divider */}
              <div className="flex items-center">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-3 text-sm text-gray-500">or</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
              
              {/* URL Input Option */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter PDF URL
                </label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={pdfUrl}
                    onChange={(e) => onPdfChange?.(e.target.value, null)}
                    placeholder="https://example.com/document.pdf"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the URL of a PDF document hosted online
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Info */}
      {uploadedFile && (
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* PDF Viewer */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span>Loading PDF...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-10">
            <div className="text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <p className="text-red-600 font-medium mb-2">Failed to load PDF</p>
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={pdfViewerUrl}
          className={cn(
            "w-full border-0",
            isFullscreen ? "h-[calc(100vh-120px)]" : "h-[600px]"
          )}
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center'
          }}
          title={title || 'PDF Document'}
        />
      </div>

      {/* Description Editor (if content is provided) */}
      {content !== undefined && onChange && (
        <div className="border-t border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={content}
            onChange={(e) => onChange(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            placeholder="Add a description for this PDF document..."
          />
        </div>
      )}

      {/* Save Button */}
      {onSave && (
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={onSave}
            className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
          >
            <Save className="w-4 h-4" />
            <span className="text-sm font-medium">Save</span>
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default PdfStep;
